/**
 * Pure layout/data helpers for the schedule Calendar view.
 * Kept separate from the renderer so the math can be unit-tested independently.
 */

import {
  SCHEDULE_CALENDAR_DEFAULT_DURATION_MIN,
  SCHEDULE_CALENDAR_FALLBACK_START_HOUR,
  SCHEDULE_CALENDAR_FALLBACK_END_HOUR,
} from "../constants";
import type { ScheduleItemRow } from "../types/db-types";

/** A schedule day-group restricted to dated entries (never null). */
export interface DatedGroup {
  date: string;
  items: ScheduleItemRow[];
}

/** A timed item placed in the calendar grid, including its lane within its overlap cluster. */
export interface TimedEntry {
  item: ScheduleItemRow;
  startMin: number;
  endMin: number;
  lane: number;
  totalLanes: number;
  /** Index of the overlap cluster this entry belongs to, in time order. */
  cluster: number;
}

/** A timed entry with its pixel position inside a day column. */
export interface PositionedEntry extends TimedEntry {
  top: number;
  height: number;
}

/** Grid measurements `positionDayEntries` needs to turn minutes into pixels. */
export interface DayColumnMetrics {
  /** First hour shown at the top of the grid. */
  startHour: number;
  /** Pixels per hour. */
  hourHeightPx: number;
  /** Smallest card height, so even a 5-minute session has room for one line. */
  minHeightPx: number;
  /** Space left between a card and the one below it. */
  gapPx: number;
}

interface SortedEntry {
  item: ScheduleItemRow;
  startMin: number;
  endMin: number;
}

/**
 * Parses an HH:MM[:SS] time string into total minutes from midnight.
 * @param time - "HH:MM" or "HH:MM:SS" or null
 * @returns Total minutes from midnight, or null if input is null/invalid
 */
export function timeToMinutes(time: string | null): number | null {
  if (!time) return null;
  const [hStr, mStr] = time.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

/** True if the item has a start_time (and therefore belongs on the time grid). */
export function isTimedItem(item: ScheduleItemRow): boolean {
  return item.start_time !== null;
}

/** A schedule day-group keyed by date, where `null` collects undated ("Unscheduled") items. */
export interface DateGroup {
  date: string | null;
  items: ScheduleItemRow[];
}

/**
 * Groups schedule items by their `date`, sorting groups ascending with undated items last.
 * Items within a group preserve their incoming order (callers pre-sort by sort_order/time).
 *
 * @param items - Flat list of schedule items.
 * @returns Date-keyed groups, dated ones ascending followed by the `null` (undated) group if any.
 */
export function groupItemsByDate(items: ScheduleItemRow[]): DateGroup[] {
  const groups = new Map<string | null, ScheduleItemRow[]>();
  for (const item of items) {
    const key = item.date;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a.localeCompare(b);
    })
    .map(([date, groupItems]) => ({ date, items: groupItems }));
}

/**
 * Lays out timed items for a single day column: clusters overlapping events
 * and assigns each event a lane index plus the total lane count for its cluster.
 * @param items - Items for one day (mix of timed and untimed; untimed are filtered out)
 * @returns Timed entries with `lane` and `totalLanes` for absolute positioning
 */
export function layoutDayItems(items: ScheduleItemRow[]): TimedEntry[] {
  const sorted: SortedEntry[] = items
    .map<SortedEntry | null>((item) => {
      const startMin = timeToMinutes(item.start_time);
      if (startMin === null) return null;
      const endRaw = timeToMinutes(item.end_time);
      const endMin = endRaw !== null && endRaw > startMin ? endRaw : startMin + SCHEDULE_CALENDAR_DEFAULT_DURATION_MIN;
      return { item, startMin, endMin };
    })
    .filter((x): x is SortedEntry => x !== null)
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  const result: TimedEntry[] = [];
  let cluster: SortedEntry[] = [];
  let clusterEnd = -Infinity;
  let clusterIndex = -1;

  const flushCluster = (): void => {
    if (cluster.length === 0) return;
    clusterIndex += 1;
    const laneEnds: number[] = [];
    const assignments: { entry: SortedEntry; lane: number }[] = [];
    for (const entry of cluster) {
      let lane = laneEnds.findIndex((end) => end <= entry.startMin);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(entry.endMin);
      } else {
        laneEnds[lane] = entry.endMin;
      }
      assignments.push({ entry, lane });
    }
    const totalLanes = laneEnds.length;
    for (const { entry, lane } of assignments) {
      result.push({
        item: entry.item,
        startMin: entry.startMin,
        endMin: entry.endMin,
        lane,
        totalLanes,
        cluster: clusterIndex,
      });
    }
  };

  for (const entry of sorted) {
    if (entry.startMin >= clusterEnd) {
      flushCluster();
      cluster = [entry];
      clusterEnd = entry.endMin;
    } else {
      cluster.push(entry);
      clusterEnd = Math.max(clusterEnd, entry.endMin);
    }
  }
  flushCluster();
  return result;
}

/**
 * Turns laid-out entries into pixel positions for one day column.
 *
 * Every card is at least `minHeightPx` tall, so a very short session (5 minutes is about 11px on
 * mobile) can be taller than its time slot. A card that would then cover the card above it in the
 * same lane, or anything in an earlier overlap cluster, is pushed down just below it. Cards with
 * room keep their exact time position; back-to-back short sessions stack instead of overlapping.
 *
 * @param entries - Output of `layoutDayItems`: clusters in time order, entries sorted by start within each
 * @param metrics - Grid start hour, pixels per hour, minimum card height and gap between cards
 * @returns Each entry with `top` and `height` in pixels
 */
export function positionDayEntries(entries: TimedEntry[], metrics: DayColumnMetrics): PositionedEntry[] {
  const { startHour, hourHeightPx, minHeightPx, gapPx } = metrics;
  const positioned: PositionedEntry[] = [];
  // Lowest card bottom of every finished cluster, and per lane within the current cluster.
  let previousClustersBottom = -Infinity;
  let currentCluster = -1;
  let currentClusterBottom = -Infinity;
  let laneBottoms = new Map<number, number>();

  for (const entry of entries) {
    if (entry.cluster !== currentCluster) {
      previousClustersBottom = Math.max(previousClustersBottom, currentClusterBottom);
      currentCluster = entry.cluster;
      currentClusterBottom = -Infinity;
      laneBottoms = new Map();
    }
    const timeTop = ((entry.startMin - startHour * 60) / 60) * hourHeightPx;
    const timeHeight = ((entry.endMin - entry.startMin) / 60) * hourHeightPx;
    const laneBottom = laneBottoms.get(entry.lane) ?? -Infinity;
    const top = Math.max(timeTop, previousClustersBottom + gapPx, laneBottom + gapPx);
    const height = Math.max(minHeightPx, timeHeight - gapPx);
    const bottom = top + height;
    laneBottoms.set(entry.lane, bottom);
    currentClusterBottom = Math.max(currentClusterBottom, bottom);
    positioned.push({ ...entry, top, height });
  }
  return positioned;
}

/**
 * Computes the displayed hour range that covers every timed item across the given groups.
 * Falls back to SCHEDULE_CALENDAR_FALLBACK_* when no timed items exist.
 * @param groups - Dated groups to inspect
 * @returns Inclusive `[startHour, endHour]` (hours of day, 0-24)
 */
export function computeHourRange(groups: DatedGroup[]): { startHour: number; endHour: number } {
  let minMin: number | null = null;
  let maxMin: number | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      const sm = timeToMinutes(item.start_time);
      if (sm === null) continue;
      const emRaw = timeToMinutes(item.end_time);
      const em = emRaw !== null && emRaw > sm ? emRaw : sm + SCHEDULE_CALENDAR_DEFAULT_DURATION_MIN;
      minMin = minMin === null ? sm : Math.min(minMin, sm);
      maxMin = maxMin === null ? em : Math.max(maxMin, em);
    }
  }
  if (minMin === null || maxMin === null) {
    return {
      startHour: SCHEDULE_CALENDAR_FALLBACK_START_HOUR,
      endHour: SCHEDULE_CALENDAR_FALLBACK_END_HOUR,
    };
  }
  const sH = Math.max(0, Math.floor(minMin / 60));
  const eH = Math.min(24, Math.ceil(maxMin / 60));
  return { startHour: sH, endHour: Math.max(eH, sH + 1) };
}

/** Returns the inclusive hour list `[startHour, startHour+1, ..., endHour]`. */
export function hoursInRange(startHour: number, endHour: number): number[] {
  const out: number[] = [];
  for (let h = startHour; h <= endHour; h++) out.push(h);
  return out;
}

/** One label in the calendar's hour gutter. */
export interface HourLabel {
  hour: number;
  /** The 12-hour number, e.g. "8" or "12". */
  label: string;
  /** "AM" or "PM" on the first label and wherever it changes; null on the rest. */
  period: "AM" | "PM" | null;
}

/**
 * Builds the hour gutter's labels: just the number, with AM/PM shown only on the first label and
 * where the period changes (noon, and midnight on a day that runs past it). Keeps the gutter narrow.
 *
 * @param hours - Grid hours in order, 0 to 24 (24 is the midnight line at the bottom of a full day)
 * @returns One label per hour, e.g. 8 AM, 9, 10, 11, 12 PM, 1
 */
export function buildHourLabels(hours: number[]): HourLabel[] {
  let previousPeriod: "AM" | "PM" | null = null;
  return hours.map((hour) => {
    const period = hour % 24 < 12 ? "AM" : "PM";
    const isChange = period !== previousPeriod;
    previousPeriod = period;
    return { hour, label: String(hour % 12 || 12), period: isChange ? period : null };
  });
}
